import {
  Button,
  ButtonProps,
  Flex,
  FlexProps,
  Input,
  Menu,
  MenuButton,
  MenuDivider,
  MenuGroup,
  MenuItem,
  MenuList,
} from '@chakra-ui/react';
import * as _ from 'lodash';
import React, { useContext, useMemo, useRef, useState } from 'react';

import { basicStringSearch } from './helpers';
import { EventLogTypeGQLType } from '../../../types/dashboard-core-gqlTypes.generated.d';
import { immer } from 'zustand/middleware/immer';
import { createWithEqualityFn } from 'zustand/traditional';
import { Counter } from '~/packages/common-frontend/components/Counter';
import {
  DateRange,
  FullDateRangePicker,
} from '~/packages/common-frontend/components/DateRangePicker';
import { Icon } from '~/packages/common-frontend/components/icons';
import { Sizes } from '~/packages/common-frontend/theme';

// Store
type EventLogFiltersState = {
  allowedEventTypes: EventLogTypeGQLType[];
  selectedEventTypes: EventLogTypeGQLType[];

  actorIds: string[];
  creditFacilityIds: string[];
  dateRange: DateRange;
  deliverableIds: string[];
  fundingRequestIds: string[];
  invoiceIds: string[];
  limitedPartnerIds: string[];
  valleyBankLoanAccountIds: string[];
};
type EventLogFiltersActions = {
  setSelectedEventTypes: (next: EventLogTypeGQLType[]) => void;

  setActorIds: (next: string[]) => void;
  setCreditFacilityIds: (next: string[]) => void;
  setDateRange: (next: DateRange) => void;
  setDeliverableIds: (next: string[]) => void;
  setFundingRequestIds: (next: string[]) => void;
  setInvoiceIds: (next: string[]) => void;
  setLimitedPartnerIds: (next: string[]) => void;
  setValleyBankLoanAccountIds: (next: string[]) => void;
};
type EventLogFiltersStore = EventLogFiltersState & EventLogFiltersActions;
type PartialInitialValues =
  // "allowedEventTypes" must be provided at initialization
  Pick<EventLogFiltersState, 'allowedEventTypes'> &
    Partial<Omit<EventLogFiltersState, 'allowedEventTypes'>>;

export const createEventLogFiltersStore = (
  partialInitialValues: PartialInitialValues,
) =>
  createWithEqualityFn<EventLogFiltersStore>()(
    immer((set) => ({
      allowedEventTypes: partialInitialValues.allowedEventTypes,

      // Pre-select all events
      selectedEventTypes:
        partialInitialValues.selectedEventTypes ??
        partialInitialValues.allowedEventTypes,

      actorIds: partialInitialValues.actorIds ?? [],
      creditFacilityIds: partialInitialValues.creditFacilityIds ?? [],
      dateRange: { startDate: null, endDate: null },
      deliverableIds: partialInitialValues.deliverableIds ?? [],
      fundingRequestIds: partialInitialValues.fundingRequestIds ?? [],
      invoiceIds: partialInitialValues.invoiceIds ?? [],
      limitedPartnerIds: partialInitialValues.limitedPartnerIds ?? [],
      valleyBankLoanAccountIds:
        partialInitialValues.valleyBankLoanAccountIds ?? [],

      setSelectedEventTypes: (selectedEventTypes) =>
        set((state) => {
          return {
            selectedEventTypes:
              selectedEventTypes.length === 0
                ? state.allowedEventTypes
                : selectedEventTypes,
          };
        }),

      setActorIds: (actorIds) => set({ actorIds }),
      setCreditFacilityIds: (creditFacilityIds) => set({ creditFacilityIds }),
      setDeliverableIds: (deliverableIds) => set({ deliverableIds }),
      setFundingRequestIds: (fundingRequestIds) => set({ fundingRequestIds }),
      setInvoiceIds: (invoiceIds) => set({ invoiceIds }),
      setLimitedPartnerIds: (limitedPartnerIds) => set({ limitedPartnerIds }),
      setValleyBankLoanAccountIds: (valleyBankLoanAccountIds) =>
        set({ valleyBankLoanAccountIds }),
      setDateRange: (dateRange) => set({ dateRange }),
    })),
  );

type EventLogFiltersContextValue = ReturnType<
  typeof createEventLogFiltersStore
>;
const EventLogFiltersContext = React.createContext<EventLogFiltersContextValue | null>(
  null,
);

type EventLogFiltersProviderProps = {
  children: React.ReactNode;
  partialInitialValues: PartialInitialValues;
};
export const EventLogFiltersProvider = ({
  children,
  partialInitialValues,
}: EventLogFiltersProviderProps) => {
  const contextValue = useMemo(
    () => createEventLogFiltersStore(partialInitialValues),
    [partialInitialValues],
  );

  return (
    <EventLogFiltersContext.Provider value={contextValue}>
      {children}
    </EventLogFiltersContext.Provider>
  );
};

export function useEventLogFilters<T>(
  selector: (store: EventLogFiltersStore) => T,
  equalityFn?: (a: T, b: T) => boolean,
) {
  const useStore = useContext(EventLogFiltersContext);
  if (!useStore) {
    throw Error(
      'Call to `useEventLogFilters` not wrapped in `EventLogFiltersProvider`',
    );
  }

  return useStore(selector, equalityFn);
}

// Components
export const FilterButton = (props: ButtonProps) => (
  <Button
    variant="secondary-muted"
    flexShrink={0}
    size="sm"
    colorScheme="gray"
    {...props}
  />
);

export const FilterContainer = (props: FlexProps) => (
  <Flex
    gap="0.5rem"
    flexWrap="wrap"
    flexShrink={0}
    paddingBottom="0.5rem"
    {...props}
  />
);

type Option<Data> = {
  id: string;
  textLabel: string;
  data: Data;
};
type EventLogFilterProps<Data> = {
  size?: Exclude<Sizes, 'lg'>;
  options: Option<Data>[];
  selectedIds?: string[];
  noneLabel?: string;
  singularLabel: string;
  pluralLabel: string;
  onClose?: (selectedIds: string[], options: Option<Data>[]) => void;
  leftIcon?: React.ComponentProps<typeof Button>['leftIcon'];
  minWidth?: React.ComponentProps<typeof Button>['minWidth'];
  enableSearchInput?: boolean;
  onSearchInputChange?: (value: string) => void;
};
export function EventLogFilter<Data>({
  size = 'sm',
  options,
  selectedIds = [],
  noneLabel,
  singularLabel,
  pluralLabel,
  onClose,
  leftIcon,
  minWidth,
  enableSearchInput = true,
  onSearchInputChange,
}: EventLogFilterProps<Data>) {
  const searchInputRef = useRef<HTMLInputElement>(null);

  const [filteredOptions, setFilteredOptions] = useState<Option<Data>[]>(
    options.filter((option) => selectedIds.includes(option.id)),
  );

  const throttledOnChange = useMemo(
    () => _.throttle((value: string) => onSearchInputChange?.(value), 500),
    [onSearchInputChange],
  );

  return (
    <Menu
      size={size}
      closeOnSelect={false}
      onClose={() => {
        // Clear search input
        onSearchInputChange?.('');
        if (searchInputRef.current) {
          searchInputRef.current.value = '';
        }

        // "Submit" selection when the menu is closed
        onClose?.(
          filteredOptions.map((option) => option.id),
          filteredOptions,
        );
      }}
    >
      <MenuButton
        rightIcon={<Icon icon="chevron-down" />}
        as={Button}
        variant="secondary-muted"
        size={size}
        flexShrink={0}
        leftIcon={leftIcon}
        minWidth={minWidth}
      >
        {(() => {
          if (filteredOptions.length === 0) {
            return noneLabel ?? `No ${pluralLabel}`;
          }

          return (
            <>
              {filteredOptions.length === 1 ? singularLabel : pluralLabel}{' '}
              <Counter size="sm" count={filteredOptions.length} maxCount={99} />
            </>
          );
        })()}
      </MenuButton>
      <MenuList zIndex="dropdown">
        {enableSearchInput && (
          <>
            <Flex paddingInline="0.5rem" paddingBlock="0rem">
              <Input
                ref={searchInputRef}
                size="sm"
                placeholder="Search..."
                onChange={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  throttledOnChange(event.target.value.trim());
                }}
              />
            </Flex>
            <MenuDivider />
          </>
        )}
        <MenuGroup>
          <MenuItem
            icon={<Icon icon="check" style={{ visibility: 'hidden' }} />}
            onClick={() =>
              setFilteredOptions((prev) =>
                prev.length === options.length ? [] : options,
              )
            }
          >
            {filteredOptions.length === options.length
              ? 'Deselect All'
              : 'Select All'}
          </MenuItem>
        </MenuGroup>
        <MenuDivider />
        <MenuGroup>
          {options
            .sort((a, b) => a.textLabel.localeCompare(b.textLabel))
            .map((option) => {
              const isSelected = filteredOptions.find(
                ({ id }) => option.id === id,
              );
              return (
                <MenuItem
                  key={option.id}
                  icon={
                    <Icon
                      icon="check"
                      style={{
                        visibility: isSelected ? 'visible' : 'hidden',
                      }}
                    />
                  }
                  onClick={() =>
                    setFilteredOptions((prev) => {
                      const next = prev.filter(({ id }) => option.id !== id);
                      // If length matches after filtering, it means we haven't
                      // filtered out any previously selected value, so we can
                      // assume we're actually trying to select a value
                      if (next.length === prev.length) {
                        next.push(option);
                      }
                      return next;
                    })
                  }
                >
                  {option.textLabel}
                </MenuItem>
              );
            })}
        </MenuGroup>
      </MenuList>
    </Menu>
  );
}

export const EventLogTypeFilter = () => {
  const allowedEventTypes = useEventLogFilters((s) => s.allowedEventTypes);
  const selectedEventTypes = useEventLogFilters((s) => s.selectedEventTypes);
  const setSelectedEventTypes = useEventLogFilters(
    (s) => s.setSelectedEventTypes,
  );

  const [searchString, setSearchString] = useState<string>('');

  const options = useMemo(
    () =>
      allowedEventTypes
        .map((id) => ({ id, textLabel: _.startCase(id), data: id }))
        .filter((option) => basicStringSearch(searchString, option.textLabel)),
    [allowedEventTypes, searchString],
  );

  if (allowedEventTypes.length < 2) return null;

  return (
    <EventLogFilter
      onSearchInputChange={setSearchString}
      leftIcon={<Icon icon="clock" />}
      minWidth="11.5rem"
      noneLabel="Any Event Type"
      singularLabel="Event Type"
      pluralLabel="Event Types"
      selectedIds={selectedEventTypes}
      options={options}
      onClose={(_selectedIds, nextOptions) =>
        setSelectedEventTypes(nextOptions.map((option) => option.data))
      }
    />
  );
};

export const EventLogDateRangeFilter = () => {
  const storeDateRange = useEventLogFilters((s) => s.dateRange);
  const storeSetDateRange = useEventLogFilters((s) => s.setDateRange);
  const [localDateRange, setLocalDateRange] = useState(storeDateRange);

  return (
    <FullDateRangePicker
      placeholderText="Any Date Range"
      size="sm"
      currentDateRange={localDateRange}
      onSelectDateRange={([startDate, endDate]) => {
        const nextDateRange = { startDate, endDate };
        setLocalDateRange(nextDateRange);

        if (startDate === null && endDate === null) {
          storeSetDateRange(nextDateRange);
        }
      }}
      onCalendarClose={() => storeSetDateRange(localDateRange)}
    />
  );
};
